if (!customElements.get('product-video-bubble')) {
  class ProductVideoBubble extends HTMLElement {
    connectedCallback() {
      this.currentIndex = 0;
      this.setupAttempts = 0;
      this.setup();
    }

    disconnectedCallback() {
      if (this._onKeyDown) {
        document.removeEventListener('keydown', this._onKeyDown);
      }

      if (this._onTriggerClick && this.trigger) {
        this.trigger.removeEventListener('click', this._onTriggerClick);
      }

      if (this._modalMoved && this.modal?.parentElement === document.body) {
        this.modal.remove();
      }

      this._ready = false;
    }

    setup() {
      if (this._ready) return;

      this.trigger = this.querySelector('[data-bubble-trigger]');
      this.modal = this.querySelector('[data-bubble-modal]');

      if (!this.trigger || !this.modal) {
        this.setupAttempts += 1;

        if (this.setupAttempts < 30) {
          requestAnimationFrame(() => this.setup());
        }

        return;
      }

      this._ready = true;

      if (this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
        this._modalMoved = true;
      }

      this.slides = Array.from(this.modal.querySelectorAll('[data-bubble-slide]'));
      this.closeButtons = this.modal.querySelectorAll('[data-bubble-close]');
      this.backdrop = this.modal.querySelector('[data-bubble-backdrop]');
      this.prevButton = this.modal.querySelector('[data-bubble-prev]');
      this.nextButton = this.modal.querySelector('[data-bubble-next]');

      this._onTriggerClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open();
      };

      this.trigger.addEventListener('click', this._onTriggerClick);
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
      this.backdrop?.addEventListener('click', () => this.close());

      this.prevButton?.addEventListener('click', () => this.showSlide(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.showSlide(this.currentIndex + 1));

      this._onKeyDown = (event) => {
        if (!this.modal?.classList.contains('is-open')) return;

        if (event.key === 'Escape') {
          this.close();
        } else if (event.key === 'ArrowLeft' && this.slides.length > 1) {
          this.showSlide(this.currentIndex - 1);
        } else if (event.key === 'ArrowRight' && this.slides.length > 1) {
          this.showSlide(this.currentIndex + 1);
        }
      };

      document.addEventListener('keydown', this._onKeyDown);
      this.schedulePreview();
    }

    schedulePreview() {
      const mount = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.mountPreview(), { timeout: 3000 });
        } else {
          setTimeout(() => this.mountPreview(), 1500);
        }
      };

      if (document.readyState === 'complete') {
        mount();
      } else {
        window.addEventListener('load', mount, { once: true });
      }
    }

    mountPreview() {
      const template = this.querySelector('template[data-bubble-preview-template]');
      if (!template) return;

      this.querySelector('[data-bubble-poster]')?.remove();
      template.replaceWith(template.content.cloneNode(true));

      if (this.modal?.classList.contains('is-open')) {
        this.pausePreview();
      }
    }

    open() {
      if (!this._ready) {
        this.setup();
      }

      if (!this.modal) return;

      const startSlide = parseInt(this.dataset.startSlide || `${this.currentIndex}`, 10);

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      this.trigger?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('product-video-bubble-open');
      this.pausePreview();
      this.showSlide(Number.isNaN(startSlide) ? this.currentIndex : startSlide);
    }

    close() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.trigger?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('product-video-bubble-open');
      this.pauseExpanded();
      this.playPreview();
    }

    showSlide(index) {
      if (!this.slides.length) return;

      if (index < 0) {
        index = this.slides.length - 1;
      } else if (index >= this.slides.length) {
        index = 0;
      }

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);

        if (!isActive) {
          this.pauseMedia(slide);
        }
      });

      this.currentIndex = index;

      if (this.prevButton) {
        this.prevButton.hidden = this.slides.length <= 1;
      }

      if (this.nextButton) {
        this.nextButton.hidden = this.slides.length <= 1;
      }

      const activeSlide = this.slides[index];

      if (activeSlide) {
        this.playMedia(activeSlide);
      }
    }

    pausePreview() {
      this.querySelectorAll('[data-bubble-preview] native-video, [data-bubble-preview] external-video').forEach((element) => {
        element.pause?.();
      });
    }

    playPreview() {
      this.querySelectorAll('[data-bubble-preview] native-video[autoplay], [data-bubble-preview] external-video[autoplay]').forEach((element) => {
        element.play?.();
      });
    }

    pauseExpanded() {
      this.slides.forEach((slide) => this.pauseMedia(slide));
    }

    pauseMedia(container) {
      container.querySelectorAll('native-video, external-video, video').forEach((element) => {
        if (typeof element.pause === 'function') {
          element.pause();
        } else if (element.tagName === 'VIDEO') {
          element.pause();
        }
      });
    }

    playMedia(container) {
      container.querySelectorAll('native-video, external-video').forEach((element) => {
        element.play?.();
      });

      container.querySelectorAll('video').forEach((video) => {
        video.play().catch(() => {});
      });
    }
  }

  customElements.define('product-video-bubble', ProductVideoBubble);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-video-bubble]');
    if (!opener) return;

    const bubble = document.querySelector('product-video-bubble');
    if (!bubble) return;

    event.preventDefault();
    bubble.open();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('product-video-bubble').forEach((element) => {
    if (typeof element.setup === 'function') {
      element.setup();
    }
  });
});
